export enum ProductTypeEnum {
  INVENTORY = 'INVENTORY',
  STORE_USE = 'STORE_USE',
  FIX_ASSET = 'FIX_ASSET'
}

export enum RequestStatusEnum {
  DRAFT = 'DRAFT',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED'
}

export enum DeliverySourceEnum {
  WAREHOUSE = 'WAREHOUSE',
  SUPPLIER = 'SUPPLIER'
}

export enum BarcodeSizeEnum {
  S = 'S',
  M = 'M',
  L = 'L'
}

export enum BarcodeStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MARK_DELETE = 'MARK_DELETE'
}

export enum PricingGroupEnum {
  STORE = 'STORE',
  DELIVERY = 'DELIVERY'
}

export enum PricingZoneEnum {
  ALL = 'ALL'
}

export enum ReplenishmentModelEnum {
  STANDARD_PO = 'STANDARD_PO',
  FRESH_LITE = 'FRESH_LITE'
}

export enum StoreStockHandlingEnum {
  STOCK = 'STOCK',
  NO_STOCK = 'NO_STOCK'
}

export enum CertificateTypeEnum {
  HALAL = 'Halal',
  TISI = 'TISI'
}

export enum PimModeEnum {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  VIEW_REQUEST = 'VIEW_REQUEST',
  VIEW = 'VIEW',
  EDIT_REQUEST = 'EDIT_REQUEST'
}
export enum PimRequestType {
  NEW = 'NEW',
  EDIT = 'EDIT'
}

export enum ProductStatusEnum {
  SELECT = 'SELECT',
  NEW = 'NEW',
  ACTIVE = 'ACTIVE',
  SEASONAL = 'SEASONAL',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  HOLD_BUY = 'HOLD_BUY',
  HOLD_BUY_SELL = 'HOLD_BUY_SELL',
  CLEAR = 'CLEAR',
  CLEAR_PROMOTION = 'CLEAR_PROMOTION',
  RECALL = 'RECALL',
  DELETE = 'DELETE'
}

/** Reasons recorded automatically when Product Status is re-derived rather than chosen. */
export enum StatusChangeReasonEnum {
  DELIVERY_SOURCE = 'Status updated due to Delivery Source change.',
  PRODUCT_CLASS = 'Status updated due to Product Class change.'
}

export enum OnlineCommerceChannelEnum {
  OWN_DELIVERY = 'OWN_DELIVERY',
  LINE_MAN = 'LINE_MAN'
}

export enum ProductAssetImageTypeEnum {
  MARKETING = 'MARKETING'
}

export enum ProductAssetImageAngleEnum {
  FRONT = 'FRONT',
  BACK = 'BACK',
  SIDE_ANGLE = 'SIDE_ANGLE'
}

export enum ProductAssetVideoTypeEnum {
  PRODUCT_OVERVIEW = 'PRODUCT_OVERVIEW',
  USAGE_SUGGESTION = 'USAGE_SUGGESTION',
  PROMOTION = 'PROMOTION'
}

export enum FreshLiteTypeEnum {
  KEEP_STOCK = 'KEEP_STOCK'
}

export const INVENTORY_SECTIONS = [
  'generalInfo',
  'barcodeAndPricing',
  'saleRestriction',
  'storeStockReturn',
  'deliverySource',
  'supplierPurchasePrice',
  'ownDeliveryContent',
  'consumerInfo',
  'productRelations',
  'sourceAndManufacturer'
] as const;

export type SectionKey = (typeof INVENTORY_SECTIONS)[number];

// Error codes the request form resolves itself: it shows a Failed modal and sends the
// user to the related section, instead of raising the global error modal.
export const SECTION_ERROR_SECTION: Record<string, SectionKey> = {
  '05013': 'sourceAndManufacturer'
};

export interface SectionItem {
  key: SectionKey;
  labelKey: string;
  icon: string;
}

export interface SectionGroup {
  groupKey: string;
  labelKey: string;
  sections: SectionItem[];
  split?: boolean;
}

export const INVENTORY_SECTION_GROUPS: SectionGroup[] = [
  {
    groupKey: 'productMasterData',
    labelKey: 'PIM.GROUP.productMasterData',
    sections: [
      { key: 'generalInfo', labelKey: 'PIM.SECTION.generalInfo', icon: 'icon-pim-general' },
      { key: 'barcodeAndPricing', labelKey: 'PIM.SECTION.barcodeAndPricing', icon: 'icon-pim-barcode' },
      { key: 'saleRestriction', labelKey: 'PIM.SECTION.saleRestriction', icon: 'icon-pim-sale-restrict' },
      { key: 'storeStockReturn', labelKey: 'PIM.SECTION.storeStockReturn', icon: 'icon-pim-storestock' },
      { key: 'deliverySource', labelKey: 'PIM.SECTION.deliverySource', icon: 'icon-pim-delivery-source' },
      {
        key: 'supplierPurchasePrice',
        labelKey: 'PIM.SECTION.supplierPurchasePrice',
        icon: 'icon-pim-supplier-purchasing-cost'
      }
    ],
    split: true
  },
  {
    groupKey: 'sourcingInformation',
    labelKey: 'PIM.GROUP.sourcingInformation',
    sections: [
      {
        key: 'sourceAndManufacturer',
        labelKey: 'PIM.SECTION.sourceAndManufacturer',
        icon: 'icon-supplier-product-master'
      }
    ],
    split: true
  },
  {
    groupKey: 'onlineCommerceData',
    labelKey: 'PIM.GROUP.onlineCommerceData',
    sections: [
      { key: 'ownDeliveryContent', labelKey: 'PIM.SECTION.ownDeliveryContent', icon: 'icon-pim-online-channel' },
      { key: 'consumerInfo', labelKey: 'PIM.SECTION.consumerInfo', icon: 'icon-pim-consumer-info' },
      { key: 'productRelations', labelKey: 'PIM.SECTION.productRelations', icon: 'icon-pim-relation' }
    ]
  }
];

export const STORE_USE_SECTION_GROUPS: SectionGroup[] = [
  {
    groupKey: 'productMasterData',
    labelKey: 'PIM.GROUP.productMasterData',
    sections: [
      { key: 'generalInfo', labelKey: 'PIM.SECTION.generalInfo', icon: 'icon-pim-general' },
      { key: 'barcodeAndPricing', labelKey: 'PIM.SECTION.barcode', icon: 'icon-pim-barcode' },
      { key: 'storeStockReturn', labelKey: 'PIM.SECTION.storeStockReturn', icon: 'icon-pim-storestock' },
      { key: 'deliverySource', labelKey: 'PIM.SECTION.deliverySource', icon: 'icon-pim-delivery-source' },
      {
        key: 'supplierPurchasePrice',
        labelKey: 'PIM.SECTION.supplierPurchasePrice',
        icon: 'icon-supplier-product-master'
      }
    ],
    split: true
  },
  {
    groupKey: 'sourcingInformation',
    labelKey: 'PIM.GROUP.sourcingInformation',
    sections: [
      { key: 'sourceAndManufacturer', labelKey: 'PIM.SECTION.sourceAndManufacturer', icon: 'icon-pim-online-channel' }
    ]
  }
];

export const FIX_ASSET_SECTION_GROUPS = STORE_USE_SECTION_GROUPS;

export enum RetailPriceStatusEnum {
  MARK_DELETE = 'MARK_DELETE'
}
